/**
 * PromptVariableRow — V14.94 : ligne de variable du prompt (contrôle select /
 * number / texte + chip d'origine + reset). Partagée par le lanceur
 * (PromptConfigPanel) et le wizard (StageScenarioCard éditable).
 */
import { Lock, RotateCcw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const ORIGIN_STYLE = {
  lanceur:          { bg: 'rgba(217,160,91,0.16)',  fg: '#F5C77E', label: 'Ajusté ici' },
  config_projet:    { bg: 'rgba(96,165,250,0.14)',  fg: '#93C5FD', label: 'Config projet' },
  lot:              { bg: 'rgba(244,114,182,0.14)', fg: '#F9A8D4', label: 'Étage du lot' },
  materiautheque:   { bg: 'rgba(94,234,212,0.12)',  fg: '#5EEAD4', label: 'Matériauthèque' },
  defaut_typologie: { bg: 'rgba(163,230,53,0.12)',  fg: '#BEF264', label: 'Défaut typologie' },
  defaut:           { bg: 'rgba(148,163,184,0.12)', fg: '#94A3B8', label: 'Défaut' },
};

export const OriginChip = ({ origin, small }) => {
  const s = ORIGIN_STYLE[origin] || ORIGIN_STYLE.defaut;
  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold uppercase tracking-wider ${small ? 'text-[8.5px] px-1.5 py-px' : 'text-[9.5px] px-2 py-0.5'}`}
      style={{ background: s.bg, color: s.fg, border: `1px solid ${s.fg}33` }}
      data-testid={`origin-chip-${origin}`}
    >
      {s.label}
    </span>
  );
};

export const VariableRow = ({ v, onChange, onReset, inStage }) => {
  const disabled = !v.editable;
  const control = (() => {
    if (disabled) {
      return (
        <span className="inline-flex items-center gap-1 text-[11.5px] truncate" style={{ color: 'var(--c-text-2)' }} title={v.note || ''}>
          <Lock className="w-3 h-3 shrink-0" style={{ color: '#F9A8D4' }} /> {String(v.value || '—')}
        </span>
      );
    }
    if (v.type === 'select' && v.options?.length) {
      const opts = v.options.includes(v.value) || !v.value ? v.options : [v.value, ...v.options];
      return (
        <Select value={v.value || ''} onValueChange={(val) => onChange(v.key, val)}>
          <SelectTrigger className="h-7 text-[11.5px] bg-white/[0.04] border-white/10 text-white" data-testid={`prompt-var-${v.key}`}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (v.type === 'number') {
      return (
        <input type="number" value={v.value ?? ''} onChange={(e) => onChange(v.key, e.target.value === '' ? '' : Number(e.target.value))}
               className="h-7 w-full px-2 text-[11.5px] rounded bg-white/[0.04] border border-white/10 text-white focus:outline-none focus:border-amber-400/60"
               data-testid={`prompt-var-${v.key}`} />
      );
    }
    return (
      <textarea rows={1} value={v.value ?? ''} onChange={(e) => onChange(v.key, e.target.value)}
                placeholder={v.origin === 'defaut' ? 'Vide → défaut typologie / Matériauthèque au rendu' : ''}
                className="min-h-[28px] w-full px-2 py-1 text-[11.5px] rounded bg-white/[0.04] border border-white/10 text-white placeholder:text-white/25 focus:outline-none focus:border-amber-400/60 resize-y"
                data-testid={`prompt-var-${v.key}`} />
    );
  })();
  return (
    <div className="grid grid-cols-[130px_1fr_auto] gap-2 items-center py-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', opacity: inStage === false ? 0.45 : 1 }}
         data-testid={`prompt-var-row-${v.key}`} data-in-stage={inStage === undefined ? undefined : String(inStage)}
         title={inStage === false ? 'Non utilisée à cette étape (figée par l\'image d\'appui)' : undefined}>
      <div className="min-w-0">
        <div className="text-[11px] truncate flex items-center gap-1" style={{ color: 'var(--c-text)' }} title={v.label}>
          {inStage && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--c-gold)' }} aria-label="Appliquée à cette étape" />}
          <span className="truncate">{v.label}</span>
        </div>
        <OriginChip origin={v.origin} small />
      </div>
      <div className="min-w-0">{control}</div>
      <button type="button" onClick={() => onReset(v.key)} disabled={v.origin !== 'lanceur'}
              className="p-1 rounded hover:bg-white/10 disabled:opacity-20" title="Revenir à la valeur de la config projet"
              data-testid={`prompt-var-reset-${v.key}`}>
        <RotateCcw className="w-3 h-3" style={{ color: 'var(--c-text-muted)' }} />
      </button>
    </div>
  );
};
